import { useSyncExternalStore } from 'react';
import Routes from './routes';

// Holds the feature-flag map fetched from /api/Features/Get (flags are evaluated
// server-side against Azure App Configuration - store: prodtrakref-features).
// Mirrors the UserInfo singleton pattern, with a small subscription layer so the
// useFeatureFlag hook re-renders consumers whenever the flag map is (re)loaded.
let flags = {};
const listeners = new Set();

const emit = () => {
    for (const listener of listeners) {
        listener();
    }
};

const FeatureFlags = {
    // Replace the whole flag map (called once at bootstrap after CheckAuthenticated).
    set(newFlags) {
        flags = newFlags || {};
        emit();
    },

    // Case-insensitive lookup; unknown flags default to false ("off").
    isEnabled(name) {
        if (!name) return false;
        if (Object.prototype.hasOwnProperty.call(flags, name)) return flags[name] === true;
        const match = Object.keys(flags).find(k => k.toLowerCase() === name.toLowerCase());
        return match ? flags[match] === true : false;
    },

    all() {
        return flags;
    },

    reset() {
        flags = {};
        emit();
    },

    // Fetches the evaluated flag map from the API and stores it. Best-effort:
    // any failure leaves flags empty (everything off) and never throws.
    async load() {
        try {
            const response = await fetch(Routes.Features.Get(), {
                method: 'GET',
                headers: { Accept: 'application/json' },
            });
            if (response.ok) {
                this.set(await response.json());
            }
        } catch (error) {
            console.error('Failed to load feature flags', error);
        }
    },

    subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};

// React hook: returns whether a named feature is enabled and re-renders when flags change.
export const useFeatureFlag = (name) =>
    useSyncExternalStore(
        FeatureFlags.subscribe,
        () => FeatureFlags.isEnabled(name),
    );

export default FeatureFlags;
